import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import queryClient from '@/lib/queryClient'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useSuspenseQuery,
} from '@tanstack/react-query'
import { getStore, getStoreThemes, updateStore, type StoreOrganizationScope } from './stores.api'
import type { Store, UpdateStoreInput } from './stores.types'

/** Without an organization, the root key matches every organization; hooks fall back to the route's. */
interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

/** The key entries the store fetchers read; the scoped key builders below set them. */
type StoreQueryKey = [AdminQueryRoot & StoreOrganizationScope]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

export const storeQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'store' }, { organizationId }),
  ],
  themes: ({ organizationId }: StoreOrganizationScope): StoreQueryKey =>
    buildScopedAdminQueryKey(storeQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'themes',
    }),
  show: ({ organizationId }: StoreOrganizationScope): StoreQueryKey =>
    buildScopedAdminQueryKey(storeQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'show',
    }),
}

export const storeQueries = {
  show: ({ organizationId }: StoreOrganizationScope) =>
    queryOptions({
      queryKey: storeQueryKeys.show({ organizationId }),
      queryFn: getStore,
      placeholderData: keepPreviousData,
    }),
  themes: ({ organizationId }: StoreOrganizationScope) =>
    queryOptions({
      queryKey: storeQueryKeys.themes({ organizationId }),
      queryFn: getStoreThemes,
      placeholderData: keepPreviousData,
      staleTime: 1000 * 60 * 10,
    }),
}

export function useStore({
  organizationId,
  ...params
}: OrganizationScope & QueryParams<ReturnType<typeof storeQueries.show>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...storeQueries.show({ organizationId: resolvedOrganizationId }),
    ...params,
  })
}

export function useStoreThemes({
  organizationId,
  ...params
}: OrganizationScope & QueryParams<ReturnType<typeof storeQueries.themes>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...storeQueries.themes({ organizationId: resolvedOrganizationId }),
    ...params,
  })
}

export function useUpdateStoreMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: UpdateStoreInput) => updateStore({ organizationId, ...values }),
    onSuccess: (store) => {
      queryClient.setQueryData<Store>(storeQueryKeys.show({ organizationId }), store)
    },
  })
}
